<template>
  <VPopup
    @click.stop
    v-if="visible"
    :visible="visible"
    :target="target"
    position="right"
    class="order-popup"
    align="right"
    show-pointer
    :offset="15"
  >
    <VForm :ref="setFormRef">
      <VFormItem>
        <VInputNumberRusV2
          :type="Qty"
          v-model="entity.positions[0].qty"
          class="qty-input"
          layout="vertical"
          :label="'Кол-во, ' + entity.positions[0].unit?.name"
          :placeholder="'Количество, ' + entity.positions[0].unit?.name"
          show-buttons
          :step="entity.positions[0]?.packageVolume?.toNumber() ?? 1"
          :disabled="isSaveDisabled"
          required
          :validate-method="validateQty"
        />

        <template #helper-text>
				<span class="text-faint-strong">
					Фасовка: по {{ entity.positions[0].packageVolume.toString() || '--' }} {{ entity.positions[0].unit.name }}
				</span>
        </template>

        <VInput
          v-model="entity.positions[0].product.code"
          class="filter-input"
          label="Товар"
          layout="vertical"
          disabled
        />
      </VFormItem>

      <VDateInput
        class="desired-receipt-date"
        label="Желаемая дата доставки"
        placeholder="Дата доставки"
        :disabled="isSaveDisabled"
        layout="vertical"
        required
        :validate-method="validateDesiredReceiptDate"
        :value="entity.positions[0].desiredReceiptDate"
        @input="onDesiredReceiptDate($event)"
      >
        <template #helper-text>
          <template v-if="isValidReceiptPeriod">
            Интервал получения {{ receiptPeriod.toString() }}
            <br>
            <template v-if="entity.positions[0].desiredReceiptDate">
              <div
                v-if="Utils.date.isWeekend(entity.positions[0].desiredReceiptDate)"
                class="warning"
              >
                Выпадает на выходной ({{ Utils.date.getWeekDayName(entity.positions[0].desiredReceiptDate, true, true) }})
              </div>
              <div v-else>
                {{ Utils.date.getWeekDayName(entity.positions[0].desiredReceiptDate) }}
              </div>
            </template>
          </template>
          <template v-else>
            <div class="warning">
              Период отгрузки истёк
            </div>
          </template>
        </template>
      </VDateInput>

      <VDateInput
        class="planned-shipment-date"
        label="Отгрузка план"
        placeholder="Дата отгрузки"
        :disabled="isSaveDisabled"
        layout="vertical"
        required
        :value="entity.positions[0].plannedShipmentDate"
        @input="value => onPlannedShipmentDate(value)"
      >
        <template #helper-text>
          <template v-if="entity.positions[0].plannedShipmentDate">
            <div
              v-if="Utils.date.isWeekend(entity.positions[0].plannedShipmentDate)"
              class="warning"
            >
              Выпадает на выходной ({{ Utils.date.getWeekDayName(entity.positions[0].plannedShipmentDate, true, true) }})
            </div>
            <div v-else>
              {{ Utils.date.getWeekDayName(entity.positions[0].plannedShipmentDate) }}
            </div>
          </template>
        </template>
      </VDateInput>

      <div class="buyer-info">
        <ul>
          <li>
            <header>Юр. лицо:</header>
            <div
              class="ellipsis"
              :title="entity.buyerCompany.name"
            >
              {{ entity.buyerCompany.name }}
            </div>
          </li>
          <li>
            <header>Склад:</header>
            <div
              class="ellipsis"
              :title="entity.buyerStore.name"
            >
              {{ entity.buyerStore.name }}
            </div>
          </li>
        </ul>
      </div>

      <div class="notes">
        <header ref="notes-target">
          Комментарий:
        </header>
        <VButton
          class="ellipsis"
          icon-postfix
          theme="link"
          @click="showNotesPopup"
        >
          <template #icon>
            <VIcon
              material="edit"
              theme="outlined"
            />
          </template>

          <div
            class="notes-text ellipsis"
            :title="entity.notes"
          >
            {{ entity.notes || 'Не указано' }}
          </div>
        </VButton>
      </div>

      <footer v-if="!isSaveDisabled">
        <div class="buttons-group">
          <VButton
            theme="primary"
            @click="startCreateOrder"
            :disabled="submitDisabled"
          >
            Начать оформление
          </VButton>

          <VButton @click="$emit('close')">
            Отмена
          </VButton>
        </div>
      </footer>

      <footer v-else>
        <div class="confirm-block">
          <VIcon material="warning_amber"/>
          <span>
          Вы собираетесь оформить заказ<br>
          на <b>сумму {{ entity.sumFullCalculated.format(entity.currencyCode) }}</b><br>
          Проверьте данные и подтвердите операцию
        </span>
        </div>
        <div class="buttons-group">
          <VButton
            theme="primary"
            :disabled="isLoading || submitDisabled"
            @click="createOrder"
          >
            Оформить заказ
          </VButton>

          <VButton @click="$emit('cancel')">
            Назад
          </VButton>
        </div>
      </footer>
    </VForm>

    <VPopup
      @click.stop
      class="notes-popup"
      :target="notesPopupTarget"
      :visible="notesPopupVisible"
      @update:visible="notesPopupVisible = false"
      position="left"
      padding="10px"
      show-pointer
    >
      <VTextarea
        ref="notes-control"
        v-model="notesValue"
      />

      <div class="buttons-group">
        <VButton
          theme="primary"
          @click="saveNote"
        >
          Сохранить
        </VButton>
        <VButton
          @click="notesPopupVisible = false"
        >
          Отменить
        </VButton>
      </div>
    </VPopup>
  </VPopup>
</template>

<style lang="scss" src="./BuyOneClickPopup.scss"></style>

<script>
import DateRange                  from '@/core/types/DateRange.js';
import Utils                      from '@/core/utils/Utils.js';
import Qty from "@/core/types/Qty.ts";
import DateSimple from "@/core/types/DateSimple.ts";

export default {
  name: 'BuyOneClickPopup',

  props: {
    target:         {
      type:    [String, HTMLElement, Object],
      default: null,
    },
    visible:        {
      type:    Boolean,
      default: false,
    },
    entity:         {
      type:    Object,
      default: null,
    },
    isSaveDisabled: {
      type:    Boolean,
      default: false,
    },
    isLoading:      {
      type:    Boolean,
      default: false,
    },
  },

  setup() {
    return {
      Qty,
      Utils
    }
  },

  data() {
    return {
      notesPopupVisible: false,
      notesPopupTarget:  null,
      notesValue:        null,

      /**
       * Ссылка на инстанс VForm. Заполняется функциональным ref (setFormRef)
       * вместо обычного ref="form" — $refs в Vue 3 не реактивен, и computed
       * submitDisabled не пересчитывался бы при изменении валидности полей.
       *
       * До маунта формы — null.
       */
      formRef:           null,
    };
  },
  computed: {
    /**
     * Кнопка сохранения блокируется, если не прошла хотя бы одна проверка:
     * любой контрол формы (кол-во, даты) либо период получения, который
     * контролами не проверяется. Завязка на form.isValid реактивная, поэтому
     * кнопка разблокируется сама, как только пользователь исправит поля.
     */
    submitDisabled() {
      if (this.isValidReceiptPeriod === false) {
        return true;
      }
      else if (this.formRef === null) {
        return false;
      }
      else {
        return this.formRef.isValid === false;
      }
    },

    preorderPosition() {
      return this.entity.positions[0].preorderPosition;
    },

    isValidReceiptPeriod() {
      const end = this.preorderPosition.preorderShipmentPeriod.end;

      if (end?.isBefore(DateSimple.today())) {
        return false;
      }
      else {
        return true;
      }
    },

    receiptPeriod() {
      if (!this.entity || !this.isValidReceiptPeriod) return null;

      // FIXME
      // странная фигня, у предзаказа период может быть сколь угодно большим
      // полезной информации для заказа в том просто нет, надо перепроверить логику
      const shipmentPeriod = this.preorderPosition.preorderShipmentPeriod;

      /** самая ранняя дата получения — сегодня плюс дни доставки */
      const earliestReceiptDate = DateSimple.today().addDays(this.entity.deliveryDays);

      let start = shipmentPeriod.start;
      let end   = shipmentPeriod.end?.addDays(this.entity.deliveryDays);

      /** start подтягиваем к самой ранней дате получения, только если он уже в прошлом;
       * с end проще — просто прибавляем дни доставки */

      if (start.isBefore(earliestReceiptDate)) {
        start = earliestReceiptDate;
      }

      return new DateRange(start, end);
    },

    qtyLeft() {
      return this.preorderPosition.qtyLeft;
    },

    shipmentPeriod() {
      if (!this.entity) return null;

      const {
        start,
        end,
      } = this.preorderPosition.preorderShipmentPeriod;

      return {
        start,
        end,
      };
    },
  },

  methods: {
    /**
     * Функциональный ref для VForm — кладёт инстанс в реактивное поле formRef.
     * Vue зовёт колбэк с инстансом при маунте и с null при размонтировании.
     */
    setFormRef(el) {
      this.formRef = el;
    },

    async startCreateOrder() {
      await this.formRef.validate();

      if (this.formRef.isValid === true && this.isValidReceiptPeriod === true) {
        this.$emit('start-create-order');
      }
    },

    onDesiredReceiptDate(value) {
      if (!value) return;
      this.entity.positions[0].desiredReceiptDate = value;

      /** Переведено на DateSimple: было new Date(value) + setDate.
       * Дату отгрузки отматываем назад на дни доставки; addDays возвращает
       * новый DateSimple, исходное значение не мутируется */
      this.entity.positions[0].plannedShipmentDate = new DateSimple(value)
        .addDays(-this.entity.positions[0].deliveryDays);
    },

    onPlannedShipmentDate(value) {
      this.entity.positions[0].plannedShipmentDate = value;

      /** Переведено на DateSimple: было new Date(value) + setDate */
      this.entity.positions[0].desiredReceiptDate = new DateSimple(value)
        .addDays(this.entity.positions[0].deliveryDays);
    },

    validateQty(value) {
      const packageVolume = this.entity.positions[0].packageVolume;

      return this.$core.utils.number.validateDesiredQty(
        value,
        packageVolume,
        this.qtyLeft, // maxQty
        packageVolume, // minQty
        this.entity.positions[0].unit.name
      );
    },

    validateDesiredReceiptDate(value) {
      if (
        !this.$core.utils.number.isNumber(this.entity.positions[0].deliveryDays)
        || !this.isValidReceiptPeriod
      ) {
        return null;
      }

      if (value < +new Date()) return 'Дата не может быть раньше текущей';

      if (!this.receiptPeriod.contains(value)) return 'Вне интервала получения';

      return null;
    },

    showNotesPopup() {
      this.notesPopupTarget = this.$refs['notes-target'];

      setTimeout(() => {
        this.notesPopupVisible = true;
        this.$nextTick(() => {
          this.$refs['notes-control'].$el.querySelector('textarea')
            .focus();
        });
      }, 0);
    },

    saveNote() {
      this.entity.notes = this.notesValue;

      this.notesPopupVisible = false;
    },

    createOrder() {
      this.$emit('create-order');
    },
  },
};
</script>
